import { Component, OnInit } from '@angular/core';
import { MatDialog, MatTableDataSource } from '@angular/material';
import { JobService } from 'src/@core/services/job/job.service';
import { PhoneNumbersComponent } from '../phone-numbers.component';

interface Pagination {
  currentPageNum: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
}

@Component({
  selector: 'hour4u-admin-purchased-phone-numbers',
  templateUrl: './purchased-phone-numbers.component.html',
  styleUrls: ['./purchased-phone-numbers.component.scss']
})
export class PurchasedPhoneNumbersComponent implements OnInit {
  phoneNumbers: any[] = [];
  isLoading: boolean = false;
  error: string = '';
  searchText: string = '';

  // Table configuration
  displayedColumns: string[] = ['phone_number', 'country', 'bought_on', 'valid_until', 'monthly_amount', 'action'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  // Pagination
  pagination: Pagination | null = null;
  resultsLength: number = 0;
  pageNumbers: (number | string)[] = [];

  constructor(
    private jobService: JobService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.fetchPhoneNumbers();
  }

  fetchPhoneNumbers(pageNum: number = 1) {
    this.error = '';
    this.isLoading = true;

    this.jobService.listPhoneNumbers().subscribe({
      next: (response: any) => {
        console.log("response", response);

        // Filter only phone numbers with active status
        const activePhones = (response.content || []).filter((phone: any) => phone.status === 'active');

        this.phoneNumbers = activePhones;
        this.dataSource.data = activePhones;
        this.resultsLength = activePhones.length;

        // Set pagination info
        this.pagination = {
          currentPageNum: pageNum,
          totalPages: Math.ceil(activePhones.length / 10),
          totalElements: activePhones.length,
          pageSize: 10
        };

        this.updatePageNumbers();
        this.isLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to fetch phone numbers.';
        this.isLoading = false;
        this.dataSource.data = [];
      }
    });
  }

  searchPhoneNumbers() {
    if (!this.searchText.trim()) {
      this.dataSource.data = this.phoneNumbers;
      return;
    }

    const searchLower = this.searchText.toLowerCase();
    this.dataSource.data = this.phoneNumbers.filter(phone =>
      phone.mobile.toLowerCase().includes(searchLower) ||
      phone.countryCode.toLowerCase().includes(searchLower)
    );
  }

  clearSearch() {
    this.searchText = '';
    this.searchPhoneNumbers();
  }

  openBuyNumberModal() {
    const dialogRef = this.dialog.open(PhoneNumbersComponent, {
      width: '512px',
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        console.log(result.message);
        // Refresh the phone numbers list after successful purchase
        this.fetchPhoneNumbers();
      }
    });
  }

  formatDate(createdOnArray: number[]): number | null {
    if (!Array.isArray(createdOnArray) || createdOnArray.length < 6) return null;

    const [year, month, day, hour, minute, second, nanosecond] = createdOnArray;
    const ms = nanosecond ? Math.floor(nanosecond / 1e6) : 0;

    return new Date(year, month - 1, day, hour, minute, second, ms).getTime();
  }

  // Pagination methods
  onPrev() {
    if (this.pagination && this.pagination.currentPageNum > 1) {
      this.fetchPhoneNumbers(this.pagination.currentPageNum - 1);
    }
  }

  onNext() {
    if (this.pagination && this.pagination.currentPageNum < this.pagination.totalPages) {
      this.fetchPhoneNumbers(this.pagination.currentPageNum + 1);
    }
  }

  onPageSelect(page: number) {
    if (typeof page === 'number') {
      this.fetchPhoneNumbers(page);
    }
  }

  updatePageNumbers() {
    if (!this.pagination) {
      this.pageNumbers = [];
      return;
    }

    const current = this.pagination.currentPageNum;
    const total = this.pagination.totalPages;
    const pages: (number | string)[] = [];

    if (total <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (current < total - 2) {
        pages.push('...');
      }

      // Always show last page
      pages.push(total);
    }

    this.pageNumbers = pages;
  }

  isEllipsis(page: number | string): boolean {
    return page === '...';
  }

  trackByPageNumber(index: number, page: number | string): any {
    return page;
  }
}
